/**
 * Phase 8 T-15 §14 C–D — Step 2: "Create your workspace"
 *   /onboarding/workspace
 *
 * Fields: logo image upload (drag-drop), name, slug.
 * Footer: Back | Skip | Continue
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Upload, X } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { orgService } from '@core/supabase/services/orgService';
import { supabase } from '@core/supabase/client';
import { B2B_CANDIDATE_RESUME_BUCKET } from '@core/supabase/storageConfig';
import type { OnboardingOutletContext } from './OnboardingLayout';

function slugify(t: string) {
  return t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 40);
}

export function OnboardingWorkspaceStep() {
  const { navigate } = useOutletContext<OnboardingOutletContext>();
  const { user } = useAuth();

  const [name, setName]         = useState('');
  const [slug, setSlug]         = useState('');
  const [logoUrl, setLogoUrl]   = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  /* Pre-fill from existing org if present */
  useEffect(() => {
    if (!user?.orgId) return;
    orgService.getOrg(user.orgId).then((o) => {
      if (o) {
        setName(o.name ?? '');
        setSlug(o.slug ?? '');
      }
    }).catch(() => {/* non-blocking */});
  }, [user?.orgId]);

  const applyFile = (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    setLogoFile(file);
    setLogoUrl(URL.createObjectURL(file));
    setError('');
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) applyFile(file);
  }, []);

  const handleContinue = async () => {
    if (!user?.orgId) { navigate('/onboarding/team'); return; }
    setSaving(true); setError('');
    try {
      /* Upload logo if selected */
      let logoPath: string | undefined;
      if (logoFile) {
        const ext  = logoFile.name.split('.').pop() ?? 'png';
        const path = `org-logos/${user.orgId}/logo.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from(B2B_CANDIDATE_RESUME_BUCKET)
          .upload(path, logoFile, { upsert: true, contentType: logoFile.type });
        if (uploadErr) throw uploadErr;
        logoPath = path;
      }

      /* Persist name / slug to org row */
      const patch: Record<string, string> = {};
      if (name.trim())      patch['name'] = name.trim();
      if (slug.trim())      patch['slug'] = slug.trim();
      if (logoPath)         patch['logo_url'] = logoPath;
      if (Object.keys(patch).length) {
        await orgService.updateOrg(user.orgId, patch as Parameters<typeof orgService.updateOrg>[1]);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed.';
      if (msg.toLowerCase().includes('slug') || msg.includes('23505')) {
        setError(`Slug "${slug}" is already taken — try adding your city, e.g. "${slug}-hyd".`);
        setSaving(false); return;
      }
      /* non-blocking for other errors — proceed */
    } finally {
      setSaving(false);
    }
    navigate('/onboarding/team');
  };

  const handleSkip = () => navigate('/onboarding/team');

  return (
    <div className="flex flex-col min-h-[480px]">

      {/* Header */}
      <div className="px-7 pt-7 pb-5 border-b border-[var(--vscode-border-primary)]">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(139,128,249,0.12)', color: '#8B80F9' }}>
            <Building2 className="w-4 h-4" />
          </div>
          <h2 className="text-[16px] font-bold vscode-text-primary">Create your workspace</h2>
        </div>
        <p className="text-[12px] vscode-text-tertiary ml-11">Step 2 of 3 — Workspace identity</p>
      </div>

      {/* Body */}
      <div className="flex-1 px-7 py-5 space-y-4 overflow-y-auto">

        {/* Image upload drop zone */}
        <div>
          <p className="text-[12px] font-medium vscode-text-secondary mb-1.5">Workspace logo</p>

          {logoUrl ? (
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo preview" className="w-16 h-16 rounded-[10px] object-cover border border-[var(--vscode-border-primary)]" />
              <div className="flex flex-col gap-1.5">
                <button type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold vscode-text-secondary hover:vscode-text-primary transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Replace
                </button>
                <button type="button"
                  onClick={() => { setLogoUrl(null); setLogoFile(null); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#EF4444] hover:underline">
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={[
                'flex flex-col items-center justify-center rounded-[10px] border-2 border-dashed cursor-pointer transition-colors py-6 gap-2',
                dragging
                  ? 'border-[#8B80F9] bg-[rgba(139,128,249,0.06)]'
                  : 'border-[var(--vscode-border-primary)] hover:border-[#8B80F9] hover:bg-[rgba(139,128,249,0.04)]',
              ].join(' ')}
            >
              <Upload className="w-5 h-5 vscode-text-tertiary" />
              <p className="text-[12px] vscode-text-secondary font-medium">Drag & drop or click to upload</p>
              <p className="text-[11px] vscode-text-tertiary">PNG, JPG — 512×512 recommended</p>
            </div>
          )}

          <input ref={fileRef} type="file" className="sr-only" accept="image/*"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) applyFile(f); }} />
        </div>

        {/* Workspace name */}
        <div>
          <label className="text-[12px] font-medium vscode-text-secondary block mb-1" htmlFor="onb-ws-name">
            Workspace name
          </label>
          <input
            id="onb-ws-name"
            className="input text-sm"
            value={name}
            onChange={(e) => { setName(e.target.value); setSlug(slugify(e.target.value)); }}
            placeholder="Apex Talent Solutions"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="text-[12px] font-medium vscode-text-secondary block mb-1" htmlFor="onb-ws-slug">
            Careers URL
          </label>
          <div className="flex items-center rounded-[10px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-input)] overflow-hidden focus-within:ring-2 focus-within:ring-[#8B80F9]/40">
            <span className="px-2.5 text-[11px] vscode-text-tertiary border-r border-[var(--vscode-border-primary)] vscode-bg-elevated flex items-center self-stretch font-mono shrink-0">
              /careers/
            </span>
            <input
              id="onb-ws-slug"
              className="min-w-0 flex-1 bg-transparent px-2.5 py-2.5 text-sm font-mono vscode-text-primary outline-none"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40))}
              placeholder="apex-talent"
              spellCheck={false}
            />
          </div>
          <p className="text-[11px] vscode-text-tertiary mt-1">Lowercase and hyphens only.</p>
        </div>

        {error && (
          <div className="rounded-[8px] border border-red-500/30 bg-red-500/[0.07] px-3 py-2.5 text-[12px] text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="px-7 py-4 border-t border-[var(--vscode-border-primary)] flex items-center gap-3">
        <button type="button" onClick={() => navigate('/onboarding/start')}
          className="flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-bold transition-colors"
          style={{ color: '#8B80F9', background: 'rgba(139,128,249,0.08)', border: '1.5px solid rgba(139,128,249,0.28)' }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button type="button" onClick={handleSkip}
          className="text-[13px] font-semibold vscode-text-tertiary hover:vscode-text-secondary transition-colors">
          Skip
        </button>
        <div className="flex-1" />
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleContinue()}
          className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, #7B6EE8 0%, #8B80F9 100%)',
            boxShadow: '0 3px 12px rgba(139,128,249,0.38)',
          }}
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>

    </div>
  );
}
